import React, { useState, Fragment, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalKeyboardShortcuts, getDefaultShortcuts } from '../../hooks/useKeyboardNavigation';
import {
  HomeIcon,
  UserGroupIcon,
  UserIcon,
  Squares2X2Icon as ViewBoardsIcon,
  CalendarIcon,
  ArrowRightOnRectangleIcon as LogoutIcon,
  Bars3Icon as MenuIcon,
  XMarkIcon,
  BuildingOfficeIcon as OfficeBuildingIcon,
  Cog8ToothIcon as CogIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import { Transition, Dialog, Menu, Disclosure } from '@headlessui/react';
import { classNames } from '../../utils/classNames';
import { ThemeToggle } from '../ui/theme-toggle';
import InstanceAlert from '../alerts/InstanceAlert';

/**
 * Props do componente MainLayout
 */
interface MainLayoutProps {
  children: React.ReactNode;
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Leads', href: '/leads', icon: UserGroupIcon },
  { name: 'Doutores', href: '/doutores', icon: UserIcon },
  { name: 'Atendentes', href: '/atendentes', icon: UserGroupIcon },
  { name: 'Agendamentos', href: '/agendamentos', icon: CalendarIcon },
  { name: 'Kanban', href: '/kanban', icon: ViewBoardsIcon },
  { name: 'Memória de Conversas', href: '/conversation-memory', icon: ChatBubbleLeftRightIcon },
];

const configItems = [
  { name: 'Clínica', href: '/configuracoes/clinicas', icon: OfficeBuildingIcon },
  { name: 'Instâncias WhatsApp', href: '/configuracoes/instancias', icon: ChatBubbleLeftRightIcon },
  { name: 'Arquivos', href: '/configuracoes/arquivos', icon: DocumentIcon },
];

/**
 * Componente de layout principal da aplicação
 * @author Matheus
 * @param children Componentes filhos a serem renderizados
 * @returns JSX.Element
 */
const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { authState, signOut } = useAuth();
  const { user } = authState;
  const email = user?.email || '';

  // Memoizar callbacks para evitar re-renders desnecessários
  const handleSidebarToggle = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const handleSidebarCollapse = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  // Memoizar navegação para evitar re-criação
  const navigationCallbacks = useMemo(() => ({
    goToDashboard: () => navigate('/dashboard'),
    goToKanban: () => navigate('/kanban'),
    goToConversations: () => navigate('/conversation-memory'),
    openSettings: () => navigate('/configuracoes/clinicas')
  }), [navigate]);

  // Configurar atalhos de teclado globais
  const shortcuts = useMemo(() => getDefaultShortcuts(navigationCallbacks), [navigationCallbacks]);

  useGlobalKeyboardShortcuts(shortcuts);

  // Memoizar data formatada
  const formattedDate = useMemo(() => {
    return new Date().toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  }, []);

  // Memoizar avatar inicial
  const avatarInitial = useMemo(() => {
    return email.charAt(0).toUpperCase() || 'U';
  }, [email]);

  // Memoizar estilos dinâmicos
  const sidebarStyles = useMemo(() => ({
    mainContentMargin: sidebarCollapsed ? 'md:ml-20' : 'md:ml-72',
    topBarLeft: sidebarCollapsed ? '80px' : '288px'
  }), [sidebarCollapsed]);

  return (
    <>
      <div className="h-screen flex bg-background w-full m-0 p-0 box-border overflow-hidden">
      {/* Mobile sidebar overlay */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 flex z-40 md:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full navbar-dental">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={handleCloseSidebar}
                  >
                    <span className="sr-only">Fechar menu</span>
                    <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex items-center px-4">
                  <div className="flex items-center">
                    <img 
                      src="/SmileIA.png" 
                      alt="SmileIA Logo" 
                      className="w-8 h-8 mr-2"
                    />
                    <span className="text-xl font-bold">
                      <span className="text-primary font-extrabold">SDR</span> <span className="text-cyan-400 font-bold">SmileIA</span>
                    </span>
                  </div>
                </div>
                <nav className="mt-8 px-4 space-y-2">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={classNames(
                        location.pathname === item.href
                          ? 'navbar-dental-link active'
                          : 'navbar-dental-link',
                      )}
                    >
                      <div className="relative">
                        <item.icon
                          className="navbar-icon"
                          aria-hidden="true"
                        />
                      </div>
                      {item.name}
                    </Link>
                  ))}
                </nav>
              </div>
              <div className="flex-shrink-0 user-profile-dental">
                <div className="flex items-center w-full">
                  <div>
                    <div className="user-avatar-dental">
                      {email.charAt(0).toUpperCase() || 'U'}
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-white">
                      {email || 'Usuário'}
                    </p>
                    <button
                      onClick={signOut}
                      className="text-xs font-medium text-white text-opacity-70 hover:text-opacity-100 transition-all flex items-center mt-1"
                    >
                      <LogoutIcon className="h-3 w-3 mr-1" />
                      Sair
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14" aria-hidden="true">
            {/* Force sidebar to shrink to fit close icon */}
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0 fixed left-0 top-0 h-full z-30">
        <div className={`sidebar-wrapper flex flex-col ${sidebarCollapsed ? 'w-20' : 'w-72'}`}>
            <div className={`flex-1 flex flex-col min-h-0 navbar-dental ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Logo */}
            <div className="logo-container">
              <div className="logo-full flex items-center">
                <img 
                  src="/SmileIA.png" 
                  alt="SmileIA Logo" 
                  className="w-8 h-8 mr-2"
                />
                <span className="text-2xl font-bold">
                  <span className="text-primary font-extrabold">SDR</span> <span className="text-cyan-400 font-bold">SmileIA</span>
                </span>
              </div>
              <div className="logo-icon flex items-center justify-center">
                <img 
                  src="/SmileIA.png" 
                  alt="SmileIA Logo" 
                  className="w-8 h-8"
                />
              </div>
            </div>
            
            <div className="flex-1 flex flex-col">
              <div className="flex-1 overflow-y-auto py-4">
                  <nav className="space-y-2 px-3">
                  {/* Links principais de navegação */}
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={classNames(
                        location.pathname === item.href
                          ? 'navbar-dental-link active'
                          : 'navbar-dental-link',
                        'sidebar-item focus-refined gpu-accelerated',
                        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-sidebar rounded-lg'
                      )}
                    >
                      <item.icon 
                        className="navbar-icon icon-hover" 
                        aria-hidden="true"
                      />
                      <span className="menu-text">{item.name}</span>
                      
                      {/* Tooltip visível apenas quando o menu está retraído */}
                      {sidebarCollapsed && (
                        <span className="menu-tooltip">{item.name}</span>
                      )}
                    </Link>
                  ))}

                  {/* Seção de Configurações */}
                  <div className="config-section mt-4">
                    <Disclosure defaultOpen={false}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button 
                            className={classNames(
                              'navbar-dental-link w-full',
                              location.pathname === '/configuracoes' ? 'active' : '',
                              'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-sidebar rounded-lg'
                            )}
                          >
                            <CogIcon 
                              className="navbar-icon" 
                              aria-hidden="true"
                            />
                            <span className="menu-text">Configurações</span>
                            {!sidebarCollapsed && (
                              <ChevronRightIcon 
                                className={classNames(
                                  'ml-auto h-4 w-4 transition-transform text-sidebar-foreground',
                                  open ? 'transform rotate-90' : ''
                                )}
                              />
                            )}
                            
                            {/* Tooltip visível apenas quando o menu está retraído */}
                            {sidebarCollapsed && (
                              <span className="menu-tooltip">Configurações</span>
                            )}
                          </Disclosure.Button>
                          
                          <Disclosure.Panel className="mt-1 space-y-1">
                            {configItems.map((item) => (
                              <Link
                                key={item.name}
                                to={item.href}
                                className={classNames(
                                  location.pathname === item.href
                                    ? 'navbar-dental-link active'
                                    : 'navbar-dental-link',
                                  !sidebarCollapsed ? 'pl-10 text-sm' : '',
                                  'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-sidebar rounded-lg'
                                )}
                              >
                                <item.icon 
                                  className="navbar-icon h-4 w-4" 
                                  aria-hidden="true"
                                />
                                <span className="menu-text">{item.name}</span>
                                
                                {/* Tooltip visível apenas quando o menu está retraído */}
                                {sidebarCollapsed && (
                                  <span className="menu-tooltip">{item.name}</span>
                                )}
                              </Link>
                            ))}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                  </nav>
              </div>

              {/* Botão de retrair antes do perfil */}
              <div className="px-3 py-2 mt-auto mb-2">
                <button
                  onClick={handleSidebarCollapse}
                  className="flex justify-center items-center w-8 h-8 mx-auto rounded-full bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent/80 transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2" 
                  aria-label={sidebarCollapsed ? "Expandir menu" : "Retrair menu"}
                  tabIndex={0}
                >
                  {sidebarCollapsed ? (
                    <ChevronRightIcon className="h-4 w-4" />
                  ) : (
                    <ChevronLeftIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              
              {/* Perfil do usuário */}
              <div className="user-profile-dental">
                <div className="user-avatar-dental">
                  {email.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="user-profile-info">
                  <p className="text-sm font-medium text-sidebar-foreground truncate">
                    {email || 'Usuário'}
                  </p>
                  <button
                    onClick={signOut}
                    className="text-xs font-medium text-sidebar-foreground opacity-70 hover:opacity-100 transition-all flex items-center mt-1 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Sair da aplicação"
                    tabIndex={0}
                  >
                    <LogoutIcon className="h-3 w-3 mr-1" />
                    Sair
                  </button>
                </div>
              </div>
            </div>
            </div>
        </div>
      </div>

      {/* Main content */}
       <div className={`flex flex-col flex-1 ${sidebarStyles.mainContentMargin}`}>
         {/* Top navigation bar */}
         <div className="fixed top-0 right-0 z-20 flex-shrink-0 flex h-20 bg-card shadow-lg border-b-2 border-border" style={{left: sidebarStyles.topBarLeft}}>
          <button
            type="button"
            className="px-4 border-r border-border text-muted-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary md:hidden hover:bg-muted transition-colors"
            onClick={handleSidebarToggle}
            aria-label="Abrir menu de navegação"
          >
            <span className="sr-only">Abrir menu</span>
            <MenuIcon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              {/* Removendo o título duplicado */}
              <div className="hidden md:block">
                 <span className="text-sm text-muted-foreground">
                   {formattedDate}
                 </span>
               </div>
            </div>
            <div className="ml-4 flex items-center md:ml-6 space-x-3">
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Profile dropdown */}
              <Menu as="div" className="relative">
                <div>
                  <Menu.Button className="max-w-xs bg-card flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary p-1 hover:bg-muted transition-all">
                    <span className="sr-only">Abrir menu do usuário</span>
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white">
                       {avatarInitial}
                     </div>
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-xl shadow-lg py-1 bg-popover ring-1 ring-border focus:outline-none">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={signOut}
                          className={classNames(
                            active ? 'bg-muted' : '',
                            'block w-full text-left px-4 py-2 text-sm text-popover-foreground'
                          )}
                        >
                          <div className="flex items-center">
                            <LogoutIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                            Sair
                          </div>
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        {/* Main content area */}
          <main className="flex-1 relative focus:outline-none bg-background pt-20 overflow-y-auto w-full m-0 p-0 box-border">
            <div className="h-full page-transition overflow-y-auto w-full m-0 p-0 box-border">
              {children}
            </div>
          </main>
      </div>
      
      {/* Instance Alert System */}
      <InstanceAlert />
      </div>
    </>
  );
};

export default MainLayout;
